import { Icon } from '../components/Icon.js';
import { RETENTION } from '../fixtures/config.js';
import type { Channel } from '../fixtures/domain.js';
import {
  deletionOf,
  deletionUrgency,
  type Deletion,
  type DeletionUrgency,
} from '../fixtures/rules.js';
import { formatCountdown } from '../util/format.js';
import { useNow } from '../util/useNow.js';
import styles from './DeletionPill.module.css';

/**
 * CSS Modules are typed as a string index, so `styles[urgency]` is unchecked in
 * both directions — renaming a DeletionUrgency member would silently yield
 * undefined and drop the styling. This map turns that into a compile error.
 */
const TONE: Record<DeletionUrgency, string> = {
  normal: styles.normal!,
  warning: styles.warning!,
  urgent: styles.urgent!,
};

/**
 * How long a list row has left, for the two lists that hold ended chats: /chat's
 * Ended rail and the widget's merged list. One component rather than one each,
 * because the same chat appears in both and they must not read differently about
 * when it disappears.
 *
 * The clock is inside, not a `now` prop, so only rows that actually draw a pill
 * subscribe to the tick. The widget's list is mostly open chats, and making it
 * re-render every second to render nothing is the cost that shape avoids. The
 * clock itself is shared (see `useNow`), so a list of ended rows still samples
 * one instant.
 */
export function DeletionPill({ channel }: { channel: Channel }) {
  const deletion = deletionOf(channel, RETENTION);
  // No hooks above this line — the countdown owns the tick, so the early return
  // is free of the rules of hooks.
  if (!deletion) return null;

  return <Countdown deletion={deletion} />;
}

/**
 * Reason picks the wording — a running countdown is the only signal the employer
 * gets that the candidate opened the closing message — but the tone comes from
 * how much time is left. Colouring by reason instead left a chat hours from
 * deletion looking calm just because nobody had read it.
 */
function Countdown({ deletion }: { deletion: Deletion }) {
  const now = useNow();
  const tone = TONE[deletionUrgency(deletion, now)];

  if (deletion.reason === 'read') {
    return (
      <span className={tone} title="The candidate read your closing message">
        <Icon name="trash" size={11} />
        {formatCountdown(deletion.at, now)}
      </span>
    );
  }

  return (
    <span className={tone} title="Deletes once the inactivity window runs out">
      Unread · {formatCountdown(deletion.at, now)}
    </span>
  );
}
